import { Capacitor } from "@capacitor/core";
import type { SupabaseClient } from "@supabase/supabase-js";
import { IndexedDbOfflineMissionStore } from "./offline-missions-indexeddb.ts";
import {
  OfflineMissionSyncEngine,
  type OfflineMissionMutation,
  type OfflineMissionStore,
} from "./offline-missions.ts";
export { listBlockingOfflineMutations } from "./offline-missions.ts";
import {
  BrowserSupabaseOfflineEvidenceTransfer,
  SupabaseOfflineMissionGateway,
  type OfflineEvidenceFileSource,
} from "./offline-missions-supabase.ts";

export type OfflineMissionRuntime = {
  storage: "indexeddb" | "sqlite";
  store: OfflineMissionStore;
  engine: OfflineMissionSyncEngine;
  saveEvidenceFile(mutation: OfflineMissionMutation, blob: Blob): Promise<void>;
  queueMutation(mutation: OfflineMissionMutation, evidenceFile?: Blob): Promise<boolean>;
  purgeProfile(profileId: string): Promise<void>;
};

type NativeEvidenceStore = OfflineEvidenceFileSource & {
  saveEvidenceFile(mutation: OfflineMissionMutation, blob: Blob): Promise<void>;
  purgeProfile(profileId: string): Promise<void>;
};

type NativeRuntimeDependencies = {
  store: OfflineMissionStore;
  evidenceStore: NativeEvidenceStore;
};

function requiredPayloadString(
  mutation: OfflineMissionMutation,
  field: "mimeType" | "originalName",
  message: string,
) {
  const value = mutation.payload[field];
  if (typeof value !== "string" || !value.trim()) throw new Error(message);
  return value;
}

function evidenceId(mutation: OfflineMissionMutation) {
  const value = mutation.payload.evidenceId ?? mutation.id;
  if (typeof value !== "string" || !value.trim()) {
    throw new Error("L’identifiant de la photo hors ligne est manquant.");
  }
  return value;
}

async function loadNativeRuntimeDependencies(): Promise<NativeRuntimeDependencies> {
  const [{ CapacitorSqliteOfflineMissionStore }, { CapacitorOfflineEvidenceFileStore }] =
    await Promise.all([
      import("./offline-missions-sqlite.ts"),
      import("./offline-missions-capacitor-files.ts"),
    ]);
  const store = new CapacitorSqliteOfflineMissionStore();
  return {
    store,
    evidenceStore: new CapacitorOfflineEvidenceFileStore(store),
  };
}

export async function createOfflineMissionRuntime(
  client: SupabaseClient,
  options?: {
    forceStorage?: "indexeddb" | "sqlite";
    fetchFile?: typeof fetch;
    loadNativeDependencies?: () => Promise<NativeRuntimeDependencies>;
  },
): Promise<OfflineMissionRuntime> {
  const storage =
    options?.forceStorage ?? (Capacitor.isNativePlatform() ? "sqlite" : "indexeddb");

  if (storage === "sqlite") {
    const native = await (
      options?.loadNativeDependencies ?? loadNativeRuntimeDependencies
    )();
    const evidenceTransfer = new BrowserSupabaseOfflineEvidenceTransfer(
      client,
      options?.fetchFile ?? fetch,
      native.evidenceStore,
    );
    const gateway = new SupabaseOfflineMissionGateway(client, { evidenceTransfer });
    const saveEvidenceFile = (mutation: OfflineMissionMutation, blob: Blob) =>
      native.evidenceStore.saveEvidenceFile(mutation, blob);
    return {
      storage,
      store: native.store,
      engine: new OfflineMissionSyncEngine(native.store, gateway),
      saveEvidenceFile,
      queueMutation: async (mutation, evidenceFile) => {
        if (mutation.kind === "evidence_added") {
          if (!evidenceFile) throw new Error("Le fichier photo hors ligne est manquant.");
          await saveEvidenceFile(mutation, evidenceFile);
        }
        try {
          return await native.store.enqueueMutation(mutation);
        } catch (error) {
          if (mutation.kind === "evidence_added") {
            const id = evidenceId(mutation);
            await native.evidenceStore.removeEvidenceFile?.(id, mutation).catch(() => undefined);
          }
          throw error;
        }
      },
      purgeProfile: async (profileId) => {
        await native.evidenceStore.purgeProfile(profileId);
        await native.store.purgeProfile(profileId);
      },
    };
  }

  const store = new IndexedDbOfflineMissionStore();
  const evidenceTransfer = new BrowserSupabaseOfflineEvidenceTransfer(
    client,
    options?.fetchFile ?? fetch,
    store,
  );
  const gateway = new SupabaseOfflineMissionGateway(client, { evidenceTransfer });
  const saveEvidenceFile = (mutation: OfflineMissionMutation, blob: Blob) =>
    store.saveEvidenceFile({
      evidenceId: evidenceId(mutation),
      profileId: mutation.profileId,
      missionId: mutation.missionId,
      blob,
      mimeType: requiredPayloadString(
        mutation,
        "mimeType",
        "Le type de la photo hors ligne est manquant.",
      ),
      originalName: requiredPayloadString(
        mutation,
        "originalName",
        "Le nom de la photo hors ligne est manquant.",
      ),
      capturedAt:
        typeof mutation.payload.capturedAt === "string" && mutation.payload.capturedAt
          ? mutation.payload.capturedAt
          : mutation.createdAt,
    });
  return {
    storage,
    store,
    engine: new OfflineMissionSyncEngine(store, gateway),
    saveEvidenceFile,
    queueMutation: async (mutation, evidenceFile) => {
      if (mutation.kind === "evidence_added") {
        if (!evidenceFile) throw new Error("Le fichier photo hors ligne est manquant.");
        await saveEvidenceFile(mutation, evidenceFile);
      }
      try {
        return await store.enqueueMutation(mutation);
      } catch (error) {
        if (mutation.kind === "evidence_added") {
          await store.removeEvidenceFile(evidenceId(mutation)).catch(() => undefined);
        }
        throw error;
      }
    },
    purgeProfile: (profileId) => store.purgeProfile(profileId),
  };
}
